import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
} from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  WorkflowManagerConfigRulesQuery,
  WorkflowManagerConfigType,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";
import { prettyPrintDateMed } from "util/dates";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { TruncateTypography } from "components/TruncateTypography";

const WORKFLOW_MANAGER_CONFIG_RULES_QUERY = gql(`
  query WorkflowManagerConfigRules($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    workflowManagerConfigRules(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        type
        value
        humanReadableValue
        notes
        createdAt
        createdBy
        updatedAt
        updatedBy
        facility {
          id
          name
        }
        tenant {
          id
          name
        }
      }
    }
  }
`);

export type WorkflowConfigRuleRow =
  WorkflowManagerConfigRulesQuery["workflowManagerConfigRules"]["items"][number];

const getTypeEmoji = (type: WorkflowManagerConfigType) => {
  switch (type) {
    case WorkflowManagerConfigType.AllowEnvironment:
    case WorkflowManagerConfigType.AllowState:
    case WorkflowManagerConfigType.AllowStateRevision:
    case WorkflowManagerConfigType.AllowFacility:
    case WorkflowManagerConfigType.AllowFacilityRevision:
      return "🟢";
    case WorkflowManagerConfigType.DenyTenant:
    case WorkflowManagerConfigType.DenyTenantRevision:
    case WorkflowManagerConfigType.DenyFacility:
    case WorkflowManagerConfigType.DenyFacilityRevision:
    case WorkflowManagerConfigType.DenyLobMailToSerc:
      return "⛔️";
    case WorkflowManagerConfigType.StateBypassValidation:
    case WorkflowManagerConfigType.FacilityBypassValidation:
      return "🔑";
    case WorkflowManagerConfigType.MaxConcurrency:
      return "✴️";
    case WorkflowManagerConfigType.MinConductExecutionTime:
      return "⏰";
    case WorkflowManagerConfigType.ForceReportTypeForSubmission:
      return "🔏";
    case WorkflowManagerConfigType.TestingFailureRate:
      return "⛓️‍💥";
    case WorkflowManagerConfigType.WebAutomationStateOverrideUseAa:
      return "🔄";
    case WorkflowManagerConfigType.Version:
      return "⚛️";
    case WorkflowManagerConfigType.SchedulerWorkIntervalSleepTimeMs:
      return "🛌";
    default:
      return "";
  }
};

const getTypeColor = (type: WorkflowManagerConfigType) => {
  switch (type) {
    case WorkflowManagerConfigType.DenyFacility:
    case WorkflowManagerConfigType.DenyTenant:
    case WorkflowManagerConfigType.DenyTenantRevision:
    case WorkflowManagerConfigType.DenyFacilityRevision:
      return "#d32f2f"; // error red
    case WorkflowManagerConfigType.AllowFacility:
    case WorkflowManagerConfigType.AllowState:
    case WorkflowManagerConfigType.AllowEnvironment:
    case WorkflowManagerConfigType.AllowFacilityRevision:
    case WorkflowManagerConfigType.AllowStateRevision:
      return "#2e7d32"; // success green
    case WorkflowManagerConfigType.MaxConcurrency:
    case WorkflowManagerConfigType.MinConductExecutionTime:
    case WorkflowManagerConfigType.TestingFailureRate:
    case WorkflowManagerConfigType.SchedulerWorkIntervalSleepTimeMs:
      return "#1976d2"; // info blue
    default:
      return "#666"; // default gray
  }
};

export function Rules() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Rules",
    },
  ]);

  const columns = useMemo(
    (): OmnisearchGridColDef<WorkflowConfigRuleRow>[] => [
      {
        field: "type",
        headerName: "Type",
        width: isMobile ? 120 : 200,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(WorkflowManagerConfigType),
        enumPresentationFunction: (enumValue: string) => {
          const type = enumValue as WorkflowManagerConfigType;
          const emoji = getTypeEmoji(type);
          const prettyValue = prettyPrintEnumValue(type);
          return emoji ? `${emoji} ${prettyValue}` : prettyValue;
        },
        renderCell: ({ row }) => {
          const emoji = getTypeEmoji(row.type);
          const prettyValue = prettyPrintEnumValue(row.type);
          return (
            <Typography variant="body2" sx={{ py: 0.5 }}>
              {emoji ? `${emoji} ${prettyValue}` : prettyValue}
            </Typography>
          );
        },
      },
      {
        field: "humanReadableValue",
        headerName: "Value",
        flex: isMobile ? 2 : 4,
        minWidth: 200,
        sortable: true,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ py: 0.5 }}>
            {value}
          </Typography>
        ),
      },
      {
        field: "rawType",
        headerName: "Raw Type",
        width: 200,
        sortable: true,
        filterable: false,
        valueGetter: (_, row) => row.type,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ py: 0.5, fontFamily: "monospace" }}>
            {value}
          </Typography>
        ),
      },
      {
        field: "rawValue",
        headerName: "Raw Value",
        width: 200,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.value,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ py: 0.5, fontFamily: "monospace" }}>
            {value}
          </Typography>
        ),
      },

      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 150,
        sortable: true,
        renderCell: ({ value }) => {
          return value ? (
            <Stack>
              <TruncateTypography
                variant="body2"
                sx={{
                  py: 0.5,
                }}
                showTooltip={true}
              >
                {value}
              </TruncateTypography>
            </Stack>
          ) : null;
        },
      },
      {
        field: "updatedBy",
        headerName: "Updated By",
        width: 120,
        sortable: true,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        width: 120,
        sortable: true,
        filterKeyType: "date",
        valueGetter: (_, row) => prettyPrintDateMed(row.updatedAt),
      },
      {
        field: "createdAt",
        headerName: "Created At",
        width: 120,
        sortable: true,
        filterKeyType: "date",
        valueGetter: (_, row) => prettyPrintDateMed(row.createdAt),
      },
    ],
    [isMobile]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="workflow-config-rules-table"
        isURLDriven
        columns={columns}
        dataQuery={WORKFLOW_MANAGER_CONFIG_RULES_QUERY}
        excludeFilterColumns={[]}
        additionalFilterColumns={[
          {
            key: "facility",
            header: "Facility",
            filterKeyType: "multipleFacility",
          },
          {
            key: "tenant",
            header: "Organization",
            filterKeyType: "multipleTenant",
          },
        ]}
        initialSortModel={[{ field: "type", sort: "asc" }]}
        getItems={(data) => data.workflowManagerConfigRules.items}
        getCount={(data) => data.workflowManagerConfigRules.count}
        noDataMessage="No workflow configuration rules found."
        initialPageSize={25}
        rowHeight={"auto"}
        columnVisibilityModel={{
          // Hide raw columns by default for all screen sizes
          rawType: false,
          rawValue: false,
          createdAt: false,
        }}
      />
    </Box>
  );
}
